import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ServerDataTable, type PagyMeta } from '@/pages/People/_components/ServerDataTable'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'
import type CrmFundraisingDonationNature from '@/types/serializers/CrmFundraisingDonationNature'
import { donationColumns } from './_components/DonationColumns'
import { CreateDonationSheet } from './_components/CreateDonationSheet'
import { DonationDetailSheet } from './_components/DonationDetailSheet'
import { useSelectedRow } from '@/lib/useSelectedRow'

interface PrefillDonor {
  id: number
  type: string
  name: string
}

interface Props {
  donations: CrmFundraisingDonation[]
  pagy: PagyMeta
  search?: string
  q?: Record<string, string>
  campaigns: CrmFundraisingCampaign[]
  payment_methods: CrmFundraisingPaymentMethod[]
  donation_natures: CrmFundraisingDonationNature[]
  donation_nature_filter_options: CrmFundraisingDonationNature[]
  prefill_donor?: PrefillDonor | null
}

export default function DonationsIndex({
  donations,
  pagy,
  search,
  q,
  campaigns,
  payment_methods,
  donation_natures,
  donation_nature_filter_options,
  prefill_donor,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const { selected, select, clear } = useSelectedRow('/fundraising/donations')
  const selectedId = selected ? Number(selected.id) : null

  // Open create sheet if ?create=true is in the URL (from opportunity show page)
  // or if the server resolved a prefill donor from ?prefill_donor_type=…&prefill_donor_id=…
  // (deep-link CTA from DonorDetailSheet).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('create') === 'true') {
      setCreateOpen(true)
    }
    if (prefill_donor) {
      setCreateOpen(true)
    }
  }, [prefill_donor])

  // Extract opportunity_id from URL params if present
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const defaultOpportunityId = urlParams?.get('opportunity_id')
    ? Number(urlParams.get('opportunity_id'))
    : undefined

  // Filter the list by nature (R8). Merges into the existing `q` so it composes
  // with the current sort; ServerDataTable preserves it across page/sort changes.
  function handleNatureFilter(value: string) {
    const nextQ: Record<string, string> = { ...(q ?? {}) }
    if (value === 'all') {
      delete nextQ.nature_id_eq
    } else {
      nextQ.nature_id_eq = value
    }

    const params: Record<string, unknown> = {}
    if (search) params.search = search
    if (Object.keys(nextQ).length > 0) params.q = nextQ

    router.get('/fundraising/donations', params, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dons</h1>
          <p className="text-muted-foreground">
            Gestion des dons et suivi des encaissements.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau don
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Select
          value={q?.nature_id_eq ?? 'all'}
          onValueChange={handleNatureFilter}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Filtrer par nature" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les natures</SelectItem>
            {donation_nature_filter_options.map((n) => (
              <SelectItem key={n.id} value={String(n.id)}>
                {n.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ServerDataTable
        columns={donationColumns}
        data={donations}
        pagy={pagy}
        basePath="/fundraising/donations"
        selectedId={selected?.id ?? null}
        onRowClick={(row) => select(row.id)}
      />

      <DonationDetailSheet
        donationId={selectedId}
        onOpenChange={(open) => { if (!open) clear() }}
      />

      <CreateDonationSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        campaigns={campaigns}
        paymentMethods={payment_methods}
        natures={donation_natures}
        defaultOpportunityId={defaultOpportunityId}
        defaultDonor={prefill_donor ?? undefined}
        lockDonor={Boolean(prefill_donor)}
      />
    </div>
  )
}
